<template>
  <AppHeaderBack :title="userData?.name || 'User Profile'" />
  <div class="min-h-screen bg-blue-50 pb-20">
    <!-- Loading state -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-20">
      <div class="w-12 h-12 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin mb-4"></div>
      <p class="text-blue-800">Loading profile...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="max-w-3xl mx-auto px-4 py-10 text-center">
      <span class="material-symbols-outlined text-5xl text-red-500 mb-2">error</span>
      <h2 class="text-xl font-bold text-gray-800 mb-2">Could not load profile</h2>
      <p class="text-gray-600 mb-6">{{ error }}</p>
      <button 
        @click="$router.go(-1)" 
        class="px-6 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
      >
        Go Back
      </button>
    </div>

    <!-- User profile content -->
    <div v-else-if="userData" class="max-w-3xl mx-auto px-4 py-6">
      <!-- Profile header with large image -->
      <div class="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
        <div class="p-6" :style="{ background: facultyGradient }">
          <div class="flex flex-col items-center">
            <!-- Large profile picture -->
            <div class="relative mb-4">
              <div class="profile-bg-ring flex items-center justify-center w-36 h-36 rounded-full">
                <img 
                  :src="userProfilePicture" 
                  :alt="userData.name || 'User'" 
                  class="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                />
              </div>
            </div>
            <!-- User name and basic info -->
            <h1 class="text-2xl font-bold text-white mb-1">{{ userData.name || 'Student' }}</h1>
            <p class="text-blue-100 mb-2">{{ getYearText(userData.university?.studyYear) }}</p>
            <div v-if="connectionStatus" class="mt-1">
              <span 
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                :class="statusBadgeClass"
              >
                <span class="material-symbols-outlined text-sm mr-1" :style="connectionIconStyle">
                  {{ connectionIcon }}
                </span>
                {{ connectionStatusText }}
              </span>
            </div>
          </div>
        </div>

        <!-- Academic information section -->
        <div class="p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Academic Information</h2>
          
          <div class="space-y-4">
            <div v-if="userData.university?.name" class="flex items-start">
              <span class="material-symbols-outlined text-blue-700 mr-3 mt-0.5">school</span>
              <div>
                <h3 class="text-sm font-medium text-gray-500 text-left">University</h3>
                <p class="text-gray-900">{{ userData.university.name }}</p>
              </div>
            </div>
            
            <div v-if="showFaculty && userData.university?.faculty" class="flex items-start">
              <span class="material-symbols-outlined text-blue-700 mr-3 mt-0.5">science</span>
              <div>
                <h3 class="text-sm font-medium text-gray-500 text-left">Faculty</h3>
                <p class="text-gray-900 text-left">{{ userData.university.faculty }}</p>
              </div>
            </div>
            
            <div v-if="showCourseName && userData.university?.courseName" class="flex items-start">
              <span class="material-symbols-outlined text-blue-700 mr-3 mt-0.5">menu_book</span>
              <div>
                <h3 class="text-sm font-medium text-gray-500 text-left">Course</h3>
                <p class="text-gray-900 text-left">{{ userData.university.courseName }}</p>
              </div>
            </div>
            
            <div v-if="showLevelOfStudy && userData.university?.levelOfStudy" class="flex items-start">
              <span class="material-symbols-outlined text-blue-700 mr-3 mt-0.5">stairs</span>
              <div>
                <h3 class="text-sm font-medium text-gray-500 text-left">Level of Study</h3>
                <p class="text-gray-900 text-left">{{ userData.university.levelOfStudy }}</p>
              </div>
            </div>
            
            <div v-if="showCampus && userData.university?.campuses && userData.university.campuses.length" class="flex items-start">
              <span class="material-symbols-outlined text-blue-700 mr-3 mt-0.5">location_on</span>
              <div>
                <h3 class="text-sm font-medium text-gray-500 text-left">Campus</h3>
                <p class="text-gray-900 text-left">{{ userData.university.campuses.join(', ') }}</p>
              </div>
            </div>
            
            <div v-if="userData.university?.modules && userData.university.modules.length" class="flex items-start">
              <span class="material-symbols-outlined text-blue-700 mr-3 mt-0.5">list_alt</span>
              <div>
                <h3 class="text-sm font-medium text-gray-500 text-left">Modules</h3>
                <div class="flex flex-wrap gap-2 mt-1">
                  <span 
                    v-for="(module, idx) in userData.university.modules" 
                    :key="idx"
                    class="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                  >
                    {{ module }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Connection and chat actions -->
      <div
        class="bg-white rounded-xl shadow-lg overflow-hidden p-6 mb-6"
        v-if="userId !== user?.uid"
      >
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Connection Options</h2>
        <div class="flex flex-col sm:flex-row gap-3">
          <!-- Connect/Pending/Connected button -->
          <button 
            v-if="connectionStatus !== 'connected'"
            @click="handleConnection" 
            class="flex-1 flex justify-center items-center py-3 px-4 rounded-lg font-medium text-white"
            :class="connectionButtonClass"
            :disabled="loading"
          >
            <span class="material-symbols-outlined mr-2">{{ connectionIcon }}</span>
            {{ connectionButtonText }}
          </button>
          
          <!-- Chat button - only show when connected -->
          <button 
            v-if="connectionStatus === 'connected'"
            @click="startChat"
            class="flex-1 flex justify-center items-center py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            :disabled="loading"
          >
            <span class="material-symbols-outlined mr-2">chat</span>
            Start Chat
          </button>
          
          <!-- Back button -->
          <button 
            @click="$router.go(-1)"
            class="flex-1 flex justify-center items-center py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            <span class="material-symbols-outlined mr-2">arrow_back</span>
            Go Back
          </button>
        </div>
      </div>
    </div>
  </div>
  <BottomNav />
</template>

<script>
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import { mapState } from 'vuex';
import defaultProfilePic from '@/assets/profileIcon.png';
import { useResources } from '@/composables/useResources';

export default {
  components: {
    AppHeaderBack,
    BottomNav
  },
  setup() {
    const { getFacultyInfo } = useResources();
    return { getFacultyInfo };
  },
  data() {
    return {
      userId: null,
      loading: true,
      error: null,
      userData: null, // Store the profile data for the viewed user here instead of in 'user'
      connectionToRemove: null,
      showRemoveConfirmation: false
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'connections', 'pendingConnections', 'connectionRequests']),
    
    userProfilePicture() {
      return this.userData?.profilePicture || defaultProfilePic;
    },
    
    facultyGradient() {
      const facultyInfo = this.getFacultyInfo(this.userData?.university?.faculty);
      return facultyInfo.gradient;
    },
    
    connectionStatus() {
      if (!this.userId) return null;
      return this.$store.getters.connectionStatusWith(this.userId);
    },
    
    connectionStatusText() {
      switch (this.connectionStatus) {
        case 'connected': return 'Connected';
        case 'pending': return 'Request Sent';
        case 'request': return 'Wants to Connect';
        default: return '';
      }
    },
    
    statusBadgeClass() {
      switch (this.connectionStatus) {
        case 'connected': return 'bg-green-100 text-green-800';
        case 'pending': return 'bg-yellow-100 text-yellow-800';
        case 'request': return 'bg-blue-100 text-blue-800 animate-pulse';
        default: return '';
      }
    },
    
    connectionIcon() {
      switch (this.connectionStatus) {
        case 'connected': return 'how_to_reg';
        case 'pending': return 'pending';
        case 'request': return 'person_add';
        default: return 'person_add';
      }
    },
    
    connectionIconStyle() {
      return this.connectionStatus === 'connected' ? { fontVariationSettings: '\'FILL\' 1' } : null;
    },
    
    connectionButtonText() {
      switch (this.connectionStatus) {
        case 'connected': return 'Connected';
        case 'pending': return 'Request Sent';
        case 'request': return 'Accept Request';
        default: return 'Connect';
      }
    },
    
    connectionButtonClass() {
      switch (this.connectionStatus) {
        case 'connected': return 'bg-green-600 hover:bg-green-700';
        case 'pending': return 'bg-yellow-600 hover:bg-yellow-700';
        case 'request': return 'bg-blue-600 hover:bg-blue-700 animate-pulse';
        default: return 'bg-blue-600 hover:bg-blue-700';
      }
    },
    
    showFaculty() {
      return this.userData?.privacySettings?.showFaculty !== false;
    },
    showCourseName() {
      return this.userData?.privacySettings?.showCourseName !== false;
    },
    showStudyYear() {
      return this.userData?.privacySettings?.showStudyYear !== false;
    },
    showLevelOfStudy() {
      return this.userData?.privacySettings?.showLevelOfStudy !== false;
    },
    showCampus() {
      return this.userData?.privacySettings?.showCampus !== false;
    }
  },
  methods: {
    getYearText(year) {
      if (!year) return 'Student';
      
      const yearNum = parseInt(year);
      if (isNaN(yearNum)) return year;
      
      const suffixes = ['st', 'nd', 'rd'];
      const suffix = yearNum <= 3 ? suffixes[yearNum - 1] : 'th';
      return `${yearNum}${suffix} Year Student`;
    },
    
    async fetchUserData() {
      this.loading = true;
      this.error = null;
      
      try {
        // Use getUserProfile instead of getUserData
        const { getUserProfile } = await import('@/composables/useFirestore');
        
        if (!getUserProfile) {
          throw new Error("getUserProfile function not found");
        }
        
        // Fetch the user profile data
        const userData = await getUserProfile(this.userId);
        
        if (userData) {
          this.userData = userData;
        } else {
          throw new Error('Could not find user profile');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        this.error = error.message || 'An error occurred while loading the profile';
      } finally {
        this.loading = false;
      }
    },
    
    async handleConnection() {
      if (this.loading) return;
      
      this.loading = true;
      
      try {
        if (this.connectionStatus === 'request') {
          await this.$store.dispatch('acceptConnectionRequest', this.userId);
          this.$store.dispatch('showAlert', {
            message: 'Connection accepted!',
            type: 'success'
          });
        } else if (!this.connectionStatus || this.connectionStatus !== 'pending') {
          await this.$store.dispatch('sendConnectionRequest', this.userId);
          this.$store.dispatch('showAlert', {
            message: 'Connection request sent!',
            type: 'success'
          });
        }
      } catch (error) {
        console.error('Error handling connection:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to update connection',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },
    
    async startChat() {
      if (!this.userId) return;
      
      const currentUser = {
        id: this.$store.state.user?.uid,
        name: this.$store.state.profile?.name || '',
        profilePicture: this.$store.state.profile?.profilePicture || ''
      };
      const otherUserDetails = {
        id: this.userId,
        name: this.userData?.name || '',
        profilePicture: this.userData?.profilePicture || ''
      };

      try {
        const { createConversation } = await import('@/composables/useChat');
        await createConversation([currentUser.id, otherUserDetails.id], [currentUser, otherUserDetails]);

        this.$router.push(`/chat/${this.userId}`);
      } catch (error) {
        console.error('Error starting chat:', error);
        this.$store.dispatch('showAlert', {
          message: 'Failed to start chat. Please try again.',
          type: 'error'
        });
      }
    }
  },
  created() {
    this.userId = this.$route.params.userId;
    if (this.userId) {
      this.fetchUserData();
    } else {
      this.error = 'No user ID provided';
      this.loading = false;
    }
  }
};
</script>

<style scoped>
@keyframes gentle-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.animate-pulse {
  animation: gentle-pulse 2s infinite;
}

.profile-bg-ring {
  background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 70%);
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.2);
}
</style>
